import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCTAButtons } from "@/hooks/useCTAButtons";
import { openTanya } from "@/hooks/useTanyaTrigger";

export interface ContentSection {
  title: string;
  subtitle?: string;
  description: string;
  image_url?: string;
  image_credit?: string;
  image_credit_url?: string;
  link_url?: string;
  link_label?: string;
  cta_key?: string;
  map_query?: string;
}

interface ContentSectionsProps {
  sections: ContentSection[];
}

export function ContentSections({ sections }: ContentSectionsProps) {
  const { data: allCTAs } = useCTAButtons();

  const renderCTA = (section: ContentSection) => {
    if (section.cta_key) {
      const cta = allCTAs?.find((c) => c.key === section.cta_key);
      if (cta) {
        return (
          <Button variant="accent" onClick={() => openTanya(cta.key)}>
            {cta.label}
            <ArrowRight className="w-4 h-4" />
          </Button>
        );
      }
    }
    if (section.link_url) {
      return (
        <Button asChild variant="accent">
          <Link to={section.link_url}>
            {section.link_label || `Learn more about ${section.title}`}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      );
    }
    return null;
  };

  // Fetch attribution opacity setting
  const { data: opacityVal } = useQuery({
    queryKey: ["image-credit-opacity"],
    queryFn: async () => {
      const { data } = await supabase
        .from("chatbot_settings")
        .select("value")
        .eq("key", "image_credit_opacity")
        .maybeSingle();
      return data?.value ? Number(data.value) : 60;
    },
    staleTime: 5 * 60 * 1000,
  });

  const opacity = (opacityVal ?? 60) / 100;

  if (sections.length === 0) return null;

  return (
    <section className="py-10 md:py-14 bg-background">
      <div className="container-padding mx-auto">
        <div className="space-y-10 md:space-y-16">
          {sections.map((section, idx) => {
            const isReversed = idx % 2 === 1;
            const hasImage = !!section.image_url;
            const isPexels = section.image_credit_url?.includes("pexels");
            const sourceName = isPexels ? "Pexels" : "Unsplash";
            const sourceUrl = isPexels
              ? "https://www.pexels.com"
              : "https://unsplash.com/?utm_source=tango_networks&utm_medium=referral";

            /* ── Full-width text-only layout when no image ── */
            if (!hasImage) {
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="max-w-3xl mx-auto"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                    {section.title}
                  </h2>
                  {section.subtitle && (
                    <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-3">
                      {section.subtitle}
                    </p>
                  )}
                  <div
                    className="text-muted-foreground text-lg leading-relaxed mb-6 prose prose-lg max-w-none rich-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.description) }}
                  />
                  {renderCTA(section)}
                </motion.div>
              );
            }

            /* ── Two-column layout with image ── */
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isReversed ? "lg:[direction:rtl]" : ""}`}
              >
                {/* Image */}
                <div className={isReversed ? "lg:[direction:ltr]" : ""}>
                  <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[3/2]">
                    <img
                      src={(() => {
                        const url = section.image_url || "/placeholder.svg";
                        if (url.includes("images.pexels.com")) {
                          // Strip any pre-existing sizing/dpr params and request a right-sized JPEG.
                          const base = url.split("?")[0];
                          return `${base}?auto=compress&cs=tinysrgb&w=800&h=534&dpr=1`;
                        }
                        if (url.includes("images.unsplash.com")) {
                          const u = url.replace(/([?&])w=\d+/g, "$1w=800").replace(/([?&])h=\d+/g, "$1h=534");
                          return u.includes("w=") ? u : `${u}${u.includes("?") ? "&" : "?"}w=800&h=534&auto=format&fit=crop`;
                        }
                        return url;
                      })()}
                      srcSet={(() => {
                        const url = section.image_url || "";
                        if (url.includes("images.pexels.com")) {
                          const base = url.split("?")[0];
                          return `${base}?auto=compress&cs=tinysrgb&w=800&h=534&dpr=1 800w, ${base}?auto=compress&cs=tinysrgb&w=1200&h=801&dpr=1 1200w`;
                        }
                        if (url.includes("images.unsplash.com")) {
                          const base = url.split("?")[0];
                          return `${base}?auto=format&fit=crop&w=800&h=534 800w, ${base}?auto=format&fit=crop&w=1200&h=801 1200w`;
                        }
                        return undefined;
                      })()}
                      sizes="(min-width: 1024px) 604px, 100vw"
                      alt={
                        section.image_credit
                          ? `${section.title} — Photo by ${section.image_credit} on ${sourceName}`
                          : section.title
                      }
                      width={800}
                      height={534}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Gradient overlay for subtitle and/or credit */}
                    {(section.subtitle || section.image_credit) && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-6 pt-12">
                        <div className="flex items-end justify-between">
                          {section.subtitle && (
                            <span className="text-white/90 font-semibold text-sm tracking-wide uppercase">
                              {section.subtitle}
                            </span>
                          )}
                          {section.image_credit && opacity > 0 && (
                            <span className="ml-auto" style={{ opacity }}>
                              <span className="text-[10px] text-white">
                                Photo by{" "}
                                {section.image_credit_url ? (
                                  <a
                                    href={section.image_credit_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:no-underline"
                                  >
                                    {section.image_credit}
                                  </a>
                                ) : (
                                  section.image_credit
                                )}
                                {" on "}
                                <a
                                  href={sourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline hover:no-underline"
                                >
                                  {sourceName}
                                </a>
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className={isReversed ? "lg:[direction:ltr]" : ""}>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                    {section.title}
                  </h2>
                  <div className="text-muted-foreground text-lg leading-relaxed mb-6 prose prose-lg max-w-none rich-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.description) }} />
                  {renderCTA(section)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
